import React, { useEffect, useState } from "react";
import { Carousel } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaArrowLeft, FaArrowRight, FaApple } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import "./categories.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

/* ------------------ ANIMATIONS ------------------ */
const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 180, damping: 18 },
  },
  hover: {
    y: -16,
    scale: 1.06,
    boxShadow: "0px 25px 50px rgba(0,0,0,0.15)",
  },
};

const imageVariants = {
  hover: {
    y: -10,
    scale: 1.12,
    transition: { type: "spring", stiffness: 160, damping: 20 },
  },
};

const titleVariants = {
  hover: { y: -4, transition: { duration: 0.25, ease: "easeOut" } },
};

/* ------------------ IMAGE HELPER ------------------ */
const getFirstImage = (product) => {
  if (!product?.images) return "/placeholder.jpg";

  // Case 1: images is array
  if (Array.isArray(product.images)) {
    return product.images[0] || "/placeholder.jpg";
  }

  // Case 2: images is object (Watch, variants, etc.)
  if (typeof product.images === "object") {
    const firstKey = Object.keys(product.images)[0];
    const imagesArray = product.images[firstKey];
    if (Array.isArray(imagesArray)) {
      return imagesArray[0] || "/placeholder.jpg";
    }
  }

  return "/placeholder.jpg";
};

/* ------------------ COMPONENT ------------------ */
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        // 1️⃣ Get category names
        const res = await fetch(`${REACT_APP_API_URL}/products/categories`);
        if (!res.ok) throw new Error("Failed to load categories");

        const categoryNames = await res.json();
        if (!Array.isArray(categoryNames)) {
          throw new Error("Invalid categories response");
        }

        // 2️⃣ Fetch ONE product per category for image
        const categoriesWithImages = await Promise.all(
          categoryNames.map(async (category) => {
            try {
              const productRes = await fetch(
                `${REACT_APP_API_URL}/products?category=${encodeURIComponent(
                  category,
                )}&limit=1`,
              );
              const products = await productRes.json();

              return {
                title: category,
                image: getFirstImage(products?.[0]),
              };
            } catch {
              return {
                title: category,
                image: "/placeholder.jpg",
              };
            }
          }),
        );

        setCategories(categoriesWithImages);
        setError(null);
      } catch (err) {
        console.error(err);
        setError(err.message);

        // 🔁 LAST RESORT FALLBACK
        setCategories([
          { title: "MacBook", image: "/placeholder.jpg" },
          { title: "Mac", image: "/placeholder.jpg" },
          { title: "iPhone", image: "/placeholder.jpg" },
          { title: "iPad", image: "/placeholder.jpg" },
          { title: "Watch", image: "/placeholder.jpg" },
          { title: "Accessories", image: "/placeholder.jpg" },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  /* ------------------ STATES ------------------ */
  if (loading) {
    return (
      <section className="category-section text-center">
        <div className="category-header ">
          <span className="text-center">Shop</span>
          <h2>By Category</h2>
        </div>
        <div className="loading-spinner">Loading categories...</div>
      </section>
    );
  }

  if (error && categories.length === 0) {
    return (
      <section className="category-section text-center">
        <div className="category-header">
          <span>Shop</span>
          <h2>By Category</h2>
        </div>
        <div className="error-message">Failed to load categories</div>
      </section>
    );
  }

  /* ------------------ CAROUSEL LOGIC ------------------ */
  const chunkSize = 4;
  const categoryChunks = [];
  for (let i = 0; i < categories.length; i += chunkSize) {
    categoryChunks.push(categories.slice(i, i + chunkSize));
  }

  return (
    <section className="category-section text-center">
      <div className="category-header">
        <span>Shop</span>
        <h2>By Category</h2>
      </div>

      {categoryChunks.length > 0 ? (
        <Carousel
          indicators={false}
          interval={10000}
          pause={false}
          prevIcon={
            <div className="custom-arrow left">
              <FaArrowLeft />
            </div>
          }
          nextIcon={
            <div className="custom-arrow right">
              <FaArrowRight />
            </div>
          }
        >
          {categoryChunks.map((chunk, slideIndex) => (
            <Carousel.Item key={slideIndex}>
              <motion.div
                className="category-row"
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                {chunk.map((cat, i) => (
                  <NavLink
                    key={`${cat.title}-${i}`}
                    to={`/shop?category=${encodeURIComponent(cat.title)}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <motion.div
                      className="category-card"
                      variants={cardVariants}
                      whileHover="hover"
                    >
                      <motion.img
                        src={cat.image}
                        alt={cat.title}
                        variants={imageVariants}
                        onError={(e) => {
                          e.target.src = "/placeholder.jpg";
                        }}
                      />
                    </motion.div>
                    <motion.div
                      className="category-title-overlay"
                      variants={titleVariants}
                    >
                      {/* <FaApple /> */}
                      <span>{cat.title}</span>
                    </motion.div>
                  </NavLink>
                ))}
              </motion.div>
            </Carousel.Item>
          ))}
        </Carousel>
      ) : (
        <div className="no-categories">No categories found</div>
      )}
    </section>
  );
};

export default Categories;
